import { usePathname, useRouter } from 'next/navigation'

import { useLocale } from '@rewardops/ui'
import {
  getCategoryByOptions,
  getCategoryPath,
  getCategoryPathDepth,
} from '@rewardops/utils'

import { useApi } from '~/components/hooks/use-api'
import { languageFromLocaleStr } from '~/app/i18n/settings'

const mapLanguages = [
  {
    key: 'en',
    value: 'English',
  },
  {
    key: 'fr',
    value: 'Français',
  },
]

export const useLocaleChange = () => {
  const { api, tenant } = useApi()
  const { locale, country, language: currentLanguage } = useLocale()
  const { data } = api.categories.getAll.useQuery()

  const pathname = usePathname()
  const router = useRouter()

  const supportedLanguages = tenant?.locale?.supported?.map(
    languageFromLocaleStr,
  )

  const availableLanguages = mapLanguages.filter((lang) =>
    supportedLanguages?.includes(lang.key),
  )

  const changeLocale = (newLanguage: string) => {
    const newLocale = `${newLanguage}-${country}`
    let newUrl = pathname?.replace(`/${locale}`, `/${newLocale}`)

    if (!newUrl) return

    // If the current page is checkout we simply redirect to shopping-cart
    if (newUrl.includes('/checkout')) {
      router.push(`/${newLocale}/shopping-cart`)
      return
    }

    if (newUrl.includes('/category/') || newUrl.includes('/items/')) {
      // In case of all categories view, no need to search root category
      if (newUrl.endsWith('/category/all')) {
        return router.push(newUrl)
      }

      // Get the root category depth to determine the depth level of the subnav
      const rootCategoryDepth = data?.rootCategory
        ? getCategoryPathDepth(data?.rootCategory)
        : 0

      const categoryPathPrefix = newUrl.includes('/category/')
        ? '/category/'
        : '/items/'

      const path = newUrl.split(categoryPathPrefix)[1]
      const categoriesNames = path?.split('/')?.filter((path) => !!path) || []
      newUrl = `/${newLocale}${categoryPathPrefix}`

      // Translates the category path. For example, the path /items/ipad/accessories
      // might be translated to /items/ipad/accessoires. Note that 'ipad' is just an example
      // and could be any (parent/sub)category that requires translation.
      categoriesNames?.forEach((catName, index) => {
        const category = getCategoryByOptions({
          categories: data?.categories,
          options: {
            path: catName,
            locale: locale,
            depthLevel: rootCategoryDepth + index + 1,
          },
        })
        
        if (!category) {
          // Branded category paths (e.g. /category/branded/wsrbc) contain brand
          // codes that don't exist in the category tree and can't be translated.
          // Preserve the original slug to avoid truncating the URL to /{locale}/category/
          // which is not a valid route and returns a 404.
          newUrl += `/${catName}/`
          return
        }

        newUrl += `/${getCategoryPath(category, newLocale)}/`
      })
    }

    router.push(newUrl)
  }

  return { currentLanguage, availableLanguages, changeLocale }
}
